import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MARKET_REFRESH_MS, playersAPI, tradesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import MarketLockBanner from '../components/MarketLockBanner';
import { PlayerPhoto } from '../components/PlayerCard';
import useMarketStatus from '../hooks/useMarketStatus';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { ArrowLeft, TrendingUp, TrendingDown, Lock } from 'lucide-react';

const IST_OFFSET_MS = 330 * 60 * 1000;

function toIstDateParts(dateLike) {
  const d = new Date(new Date(dateLike).getTime() + IST_OFFSET_MS);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
}

function istToUtcMs(year, month, day, hour, minute = 0) {
  return Date.UTC(year, month - 1, day, hour, minute) - IST_OFFSET_MS;
}

function addIstDays(parts, daysToAdd) {
  const utcMs = istToUtcMs(parts.year, parts.month, parts.day, 12, 0);
  return toIstDateParts(utcMs + daysToAdd * 24 * 60 * 60 * 1000);
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem',
    }}>
      <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 700, color: 'var(--accent)' }}>{payload[0].value.toFixed(1)} pts</div>
    </div>
  );
}

export default function PlayerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateAfterTrade } = useAuth();
  const [player, setPlayer] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [trading, setTrading] = useState(false);
  const { status: marketStatus } = useMarketStatus();

  const fetchData = useCallback(async () => {
    try {
      const [pRes, portRes] = await Promise.all([
        playersAPI.getOne(id),
        playersAPI.getPortfolio(),
      ]);
      setPlayer(pRes.data);
      setPortfolio(portRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const holding = portfolio.find(h => h.player_id === parseInt(id));

  const handleTrade = async (type) => {
    if (marketStatus.isLive) {
      setToast({ msg: 'Trading is paused during a live match', type: 'error' });
      return;
    }
    setTrading(true);
    try {
      const r = type === 'buy'
        ? await tradesAPI.buy(parseInt(id))
        : await tradesAPI.sell(parseInt(id));
      updateAfterTrade(r.data);
      setToast({ msg: r.data.message, type: 'success' });
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.error || 'Trade failed';
      setToast({ msg, type: 'error' });
    } finally {
      setTrading(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!player) return (
    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
      Player not found
    </div>
  );

  const teamColor = player.team_color || '#3b82f6';
  const changePct = parseFloat(player.price_change_pct || 0);
  const pnl = holding ? parseFloat(player.current_stock_price) - parseFloat(holding.buy_price) : null;

  const nowIst = toIstDateParts(Date.now());
  const tradingDay = (nowIst.hour < 1 || (nowIst.hour === 1 && nowIst.minute <= 30))
    ? addIstDays(nowIst, -1)
    : nowIst;

  const windowStartMs = istToUtcMs(tradingDay.year, tradingDay.month, tradingDay.day, 14, 30);
  const nextDay = addIstDays(tradingDay, 1);
  const windowEndMs = istToUtcMs(nextDay.year, nextDay.month, nextDay.day, 1, 30);

  const chartData = (player.priceHistory || [])
    .filter(h => {
      const ms = new Date(h.recorded_at).getTime();
      return ms >= windowStartMs && ms <= windowEndMs;
    })
    .map(h => ({
      ts: new Date(h.recorded_at).getTime(),
      time: new Date(h.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }),
      price: parseFloat(h.price),
    }))
    .sort((a, b) => a.ts - b.ts);

  const dayBase = parseFloat(player.day_open_price || player.base_price || player.current_stock_price || 0);
  if (dayBase > 0 && (chartData.length === 0 || Math.abs(chartData[0].ts - windowStartMs) > 60000)) {
    chartData.unshift({
      ts: windowStartMs,
      time: '02:30 PM',
      price: dayBase,
    });
  }

  const buyPrice = holding ? parseFloat(holding.buy_price) : null;

  const srLive = player.live_balls_faced > 0
    ? ((player.live_runs / player.live_balls_faced) * 100).toFixed(1)
    : null;
  const economy = player.live_overs_bowled > 0
    ? (player.live_runs_given / player.live_overs_bowled).toFixed(2)
    : null;

  const isLocked = marketStatus.isLive;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', color: 'var(--text-secondary)',
          cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem',
        }}
      >
        <ArrowLeft size={16} /> Back
      </button>

      {isLocked && (
        <MarketLockBanner
          matchLabel={marketStatus.matchLabel}
          lockedAt={marketStatus.lockedAt}
        />
      )}

      {/* Player header */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <PlayerPhoto player={player} size={56} />

          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                {player.name}
              </h2>
              {player.is_playing_today && (
                <span className="live-badge"><span className="live-dot" /> LIVE</span>
              )}
            </div>
            <div style={{
              fontSize: '0.8rem', color: 'var(--text-secondary)',
              marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap',
            }}>
              <span style={{ color: teamColor, fontWeight: 600 }}>{player.team_name}</span>
              <span>·</span>
              <span style={{ textTransform: 'capitalize' }}>{player.role.replace('-', ' ')}</span>
              <span>·</span>
              <span>{player.nationality}</span>
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 700 }}>
              {parseFloat(player.current_stock_price).toFixed(1)}
              <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}> pts</span>
            </div>
            <div style={{
              fontSize: '0.8rem',
              color: changePct >= 0 ? 'var(--green)' : 'var(--red)',
              display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end',
            }}>
              {changePct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
              {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}% today
            </div>
          </div>
        </div>
      </div>

      {/* Career stats */}
      <div className="metrics-row" style={{ marginBottom: 12 }}>
        <div className="metric-card">
          <div className="metric-label">Base Price</div>
          <div className="metric-value">{parseFloat(player.base_price).toFixed(0)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">IPL Runs</div>
          <div className="metric-value">{(player.career_runs || 0).toLocaleString()}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">IPL Wkts</div>
          <div className="metric-value">{player.career_wickets || 0}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Strike Rate</div>
          <div className="metric-value">{parseFloat(player.career_sr || 0).toFixed(1)}</div>
        </div>
      </div>

      {/* Live match stats */}
      {player.is_playing_today && (
        <div className="card" style={{ marginBottom: 12, borderColor: 'rgba(239,68,68,0.3)' }}>
          <div style={{
            fontSize: '0.75rem', color: 'var(--red)', fontWeight: 600,
            marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span className="live-dot" style={{ width: 8, height: 8 }} />
            LIVE MATCH STATS · updates every 5 min
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 10 }}>
            {(player.live_runs > 0 || player.live_balls_faced > 0) && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Runs</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {player.live_runs}
                </div>
                {player.live_balls_faced > 0 && (
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                    {player.live_balls_faced} balls
                  </div>
                )}
              </div>
            )}
            {srLive && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Live SR</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {srLive}
                </div>
              </div>
            )}
            {player.live_wickets > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Wickets</div>
                <div style={{
                  fontFamily: 'var(--font-display)', fontSize: '1.4rem',
                  fontWeight: 700, color: 'var(--accent)',
                }}>
                  {player.live_wickets}
                </div>
              </div>
            )}
            {economy && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Economy</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {economy}
                </div>
              </div>
            )}
            {player.live_catches > 0 && (
              <div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Catches</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', fontWeight: 700 }}>
                  {player.live_catches}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Price chart */}
      {chartData.length > 1 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 10,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Price History
          </div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: '#475569' }}
                  tickLine={false} axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#475569' }}
                  tickLine={false} axisLine={false}
                  domain={['auto', 'auto']}
                  width={45}
                  tickFormatter={v => v.toFixed(0)}
                />
                <Tooltip content={<CustomTooltip />} />
                {buyPrice && (
                  <ReferenceLine
                    y={buyPrice}
                    stroke="rgba(245,158,11,0.5)"
                    strokeDasharray="4 4"
                    label={{ value: 'Bought', fill: '#f59e0b', fontSize: 10, position: 'insideTopRight' }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke={changePct >= 0 ? '#10b981' : '#ef4444'}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Your position */}
      {holding && (
        <div className="card" style={{
          marginBottom: 12,
          borderColor: pnl >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
        }}>
          <div style={{
            fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8,
            fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            Your Position
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Bought @ {parseFloat(holding.buy_price).toFixed(1)} pts
              </div>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700,
                color: pnl >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 4,
              }}>
                {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)} pts
                ({parseFloat(holding.pnl_pct || 0).toFixed(1)}%)
              </div>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>1 unit</div>
          </div>
        </div>
      )}

      {/* Trade button */}
      <div style={{ display: 'flex', gap: 10 }}>
        {holding ? (
          <button
            className="btn btn-sell"
            style={{ flex: 1, padding: '13px', opacity: isLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => handleTrade('sell')}
            disabled={trading || isLocked}
          >
            {isLocked && <Lock size={14} />}
            {trading ? 'Processing...' : isLocked ? 'Trading Paused' : `Sell · ${parseFloat(player.current_stock_price).toFixed(1)} pts`}
          </button>
        ) : (
          <button
            className="btn btn-buy"
            style={{ flex: 1, padding: '13px', opacity: isLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => handleTrade('buy')}
            disabled={trading || isLocked}
          >
            {isLocked && <Lock size={14} />}
            {trading ? 'Processing...' : isLocked ? 'Trading Paused' : `Buy · ${parseFloat(player.current_stock_price).toFixed(1)} pts`}
          </button>
        )}
      </div>

      {/* Price formula explainer */}
      <div style={{
        marginTop: '1.5rem', padding: '1rem',
        background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
        fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.7,
      }}>
        <div style={{ fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
          How this player's price moves
        </div>
        {['batsman', 'all-rounder', 'wicket-keeper'].includes(player.role) && (
          <div>Batting: +0.5% per run · +5% per six · +2% per four · milestone bonuses at 30/50/75/100</div>
        )}
        {['bowler', 'all-rounder'].includes(player.role) && (
          <div>Bowling: +8% per wicket · economy bonus/penalty vs 7.0 base · haul bonus at 3/4/5 wickets</div>
        )}
        <div>Playing today: +10% match participation boost · capped at ±40% from base price</div>
      </div>
    </div>
  );
}
